import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FEATURE_REQUEST_MAX_LENGTH } from '@awwyeah/shared';
import { sendFeatureRequest } from './api';
import { Sheet } from './Sheet';
import { readDraft, writeDraft } from './storage';
import { useFeedbackStatus } from './useFeedbackStatus';

/**
 * The quiet "Ideas for AwwYeah? Tell Dan" link at the foot of every signed-in screen
 * (owner request 2026-11-03), and the little sheet it opens. Hidden for the recipient
 * (Dan doesn't message himself) and the bootstrap account — the server decides, via
 * `/api/feedback/status`.
 *
 * Offline-first-ish: the draft lives in state *and* on this device, and is only cleared
 * once the server has it. A failed send says so and leaves the words where they were.
 * No counters, no reminders — it's there when someone has an idea, invisible otherwise.
 */
export function FeedbackFooter(): JSX.Element | null {
  const status = useFeedbackStatus();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(readDraft);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => writeDraft(draft), [draft]);

  useEffect(() => {
    if (open && !sent) textareaRef.current?.focus();
  }, [open, sent]);

  const close = useCallback(() => {
    setOpen(false);
    setSent(false);
    setError(null);
  }, []);

  if (!status?.canSend) return null;
  const recipient = status.recipientName ?? 'Dan';

  const send = async (): Promise<void> => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      await sendFeatureRequest(text, `${location.pathname}${location.search}`.slice(0, 300));
      setDraft('');
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Didn't send. Your note is still here.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="mt-10 flex justify-center pb-2" data-testid="feedback-footer">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="min-h-[44px] px-3 text-xs text-fg-3 underline-offset-4 ease-app hover:text-fg-2 hover:underline"
        data-testid="feedback-open"
      >
        Ideas for AwwYeah? Tell {recipient}
      </button>

      <Sheet
        open={open}
        onClose={close}
        title={sent ? 'Sent — thank you!' : `Tell ${recipient} an idea`}
        testId="feedback-sheet"
      >
        {sent ? (
          <div className="space-y-4 pb-2" data-testid="feedback-thanks">
            <p className="text-sm leading-relaxed text-fg-2">
              {recipient} just got a little ping with your idea. Thanks for helping make AwwYeah
              yours. 💛
            </p>
            <button
              type="button"
              onClick={close}
              className="min-h-[44px] w-full rounded-xl bg-inverse px-4 py-2 text-sm font-semibold text-inverse-fg"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            className="space-y-3 pb-2"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <label className="block space-y-1">
              <span className="text-sm text-fg-2">What should AwwYeah do?</span>
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
                }}
                maxLength={FEATURE_REQUEST_MAX_LENGTH}
                rows={4}
                placeholder="Hey, could it…"
                className="block w-full resize-y rounded-xl border border-line bg-bg px-3 py-2 text-base text-fg placeholder:text-fg-3"
                data-testid="feedback-text"
              />
            </label>
            {error && (
              <p className="text-sm text-danger" role="alert" data-testid="feedback-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              className="min-h-[44px] w-full rounded-xl bg-inverse px-4 py-2 text-sm font-semibold text-inverse-fg disabled:opacity-60"
              data-testid="feedback-send"
            >
              {sending ? 'Sending…' : `Send to ${recipient}`}
            </button>
          </form>
        )}
      </Sheet>
    </footer>
  );
}
